import { Chip, Tooltip } from '@mui/material';
import { Row } from '../../shared/Panel';
import {
  EXECUTION_DEPARTMENT_LABELS,
  isExecutionDepartment,
} from '../executionDepartment/executionDepartmentApi';

/**
 * Kto prowadzi zlecenie i kto je realizuje według TMS — odpowiedzi
 * Marathonu/Euvic 16.10.2026:
 *
 * - **„Prowadzi”** (pyt. 55, praca dwutorowa, przełącznik `tradeDualTrack`):
 *   dział stanu zlecenia ze słownika — „decyduje osoba, która ma podpięte
 *   zlecenie”. API dokłada pole `leadDepartment` tylko z przełącznikiem.
 * - **„wg TMS”** (pyt. 4a i 30, przełącznik `executionDepartmentHint`):
 *   dział z mapowania wartości pola TMS „Dział odpowiedzialny”
 *   (`executionDepartmentHint`). Tylko podpowiedź — do Trade przekazuje
 *   człowiek.
 *
 * O obu rozstrzyga API: bez przełącznika pola w odpowiedzi nie ma, więc
 * nie ma też znacznika. Front niczego tu nie liczy.
 */

type Loose = Record<string, unknown>;

/** Pole niesione przez API tylko przy włączonym przełączniku `tradeDualTrack`. */
export const LEAD_DEPARTMENT_FIELD = 'leadDepartment';
/** Pole niesione przez API tylko przy włączonym przełączniku `executionDepartmentHint`. */
export const EXECUTION_HINT_FIELD = 'executionDepartmentHint';

/** Wyjaśnienie znacznika „wg TMS” w dymku. */
export const EXECUTION_HINT_TOOLTIP =
  'Kto realizuje zlecenie według pola TMS „Dział odpowiedzialny” — podpowiedź z ustawień „Kto realizuje wg TMS”. Do Trade przekazuje człowiek.';

/** Wyjaśnienie „Prowadzi” w dymku. */
export const LEAD_DEPARTMENT_TOOLTIP =
  'Dział, w którego stanie jest zlecenie — decyduje osoba, która ma je podpięte (praca dwutorowa flota i Trade).';

/** Czy API niesie „Prowadzi” (przełącznik `tradeDualTrack` włączony). */
export const hasLeadDepartment = (r: Loose | undefined | null): boolean =>
  !!r && Object.prototype.hasOwnProperty.call(r, LEAD_DEPARTMENT_FIELD);

/** „flota” / „Trade”; dział spoza listy — jak przyszedł; brak działu — archiwum wspólne. */
export function departmentName(v: unknown): string {
  if (isExecutionDepartment(v)) return EXECUTION_DEPARTMENT_LABELS[v];
  if (typeof v === 'string' && v !== '') return v;
  return 'wspólne archiwum (bez działu)';
}

/** „Prowadzi: Trade” — `null`, gdy API pola nie niesie (przełącznik wyłączony). */
export function leadDepartmentLabel(r: Loose | undefined | null): string | null {
  if (!r || !hasLeadDepartment(r)) return null;
  return `Prowadzi: ${departmentName(r[LEAD_DEPARTMENT_FIELD])}`;
}

/**
 * „wg TMS: Trade” — `null`, gdy brak podpowiedzi (pole puste, wartość bez
 * mapowania) albo przełącznik wyłączony. `short` — sama nazwa działu, do
 * kolumny „TMS” listy (szerokość przy 1366 px).
 */
export function executionHintText(v: unknown, short = false): string | null {
  if (!isExecutionDepartment(v)) return null;
  const name = EXECUTION_DEPARTMENT_LABELS[v];
  return short ? name : `wg TMS: ${name}`;
}

/** Znacznik „wg TMS: …” (albo sam dział w kolumnie „TMS”). */
export const ExecutionHintChip = ({ record, short = false }: { record: Loose; short?: boolean }) => {
  const label = executionHintText(record[EXECUTION_HINT_FIELD], short);
  if (!label) return null;
  return (
    <Tooltip title={EXECUTION_HINT_TOOLTIP}>
      <Chip size="small" variant="outlined" color="secondary" label={label} data-testid="execution-hint" />
    </Tooltip>
  );
};

/** Znacznik „Prowadzi: …”. */
export const LeadDepartmentChip = ({ record }: { record: Loose }) => {
  const label = leadDepartmentLabel(record);
  if (!label) return null;
  return (
    <Tooltip title={LEAD_DEPARTMENT_TOOLTIP}>
      <Chip size="small" variant="outlined" label={label} data-testid="lead-department" />
    </Tooltip>
  );
};

/** Wiersze boksu „Stan” karty — każdy tylko, gdy API niesie swoje pole. */
export const ProcessLeadRows = ({ record }: { record: Loose }) => {
  const hint = executionHintText(record[EXECUTION_HINT_FIELD]);
  const executionOn = Object.prototype.hasOwnProperty.call(record, EXECUTION_HINT_FIELD);
  return (
    <>
      {hasLeadDepartment(record) ? (
        <Row label="Prowadzi">{departmentName(record[LEAD_DEPARTMENT_FIELD])}</Row>
      ) : null}
      {executionOn ? (
        <Row label="Realizacja wg TMS">
          {hint ? departmentName(record[EXECUTION_HINT_FIELD]) : '— (pole puste albo bez ustawienia)'}
        </Row>
      ) : null}
    </>
  );
};
